/* ══════════════════════════════════════
   Monmouth Cards — Search JS
   assets/monmouth-search.js
   ══════════════════════════════════════ */
(function () {
  'use strict';

  /* ── Format chips on the search page ──
     Only these Product type values are offered as chips (matched case-insensitively; the label is printed the way Shopify
     returns it). A chip also needs results in the current search (the server only prints values with a count), "All" is
     always there, and a chip that is currently selected is always kept so it can be unselected. Edit this list to change
     which formats appear. */
  var MC_SEARCH_FORMAT_CHIPS = [
    'Hobby', 'Blaster', 'Mega', 'Jumbo', 'Booster Box', 'ETB',
    'Booster Bundle', 'Booster Pack', 'Display Box', 'Hanger', 'Retail'
  ];

  /* ── Predictive Search Dropdown ──
     Binds to every search input on the page. The header uses
     .mc-nav__search-input, the mobile drawer uses #mc-mobile-search-input,
     and the search page uses .mc-search-bar__input. Each gets its own
     dropdown element injected right after its form so results are scoped. */
  var debounceTimer  = null;

  /* Real race-condition fix (referral-code-race-condition-add-to-cart-
     prompt.md) — reads the mc_ref_code cookie set by layout/theme.liquid
     and returns it as a cart-attributes fragment to merge into a /cart/
     add.js call, so the code lands in the SAME request that adds the
     item instead of racing a separate background sync call. */
  function mcGetReferralAttribute() {
    var m = document.cookie.match(/(?:^|; )mc_ref_code=([^;]*)/);
    return m ? { mc_ref_code: decodeURIComponent(m[1]) } : {};
  }

  function debounce(fn, ms) {
    return function () {
      var args = arguments;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () { fn.apply(null, args); }, ms);
    };
  }

  function closeDropdown(dropdown) {
    if (dropdown) {
      dropdown.classList.remove('mc-search-dropdown--open');
      dropdown.innerHTML = '';
    }
  }

  function buildDropdownHTML(data, query) {
    var html = '';

    /* Collection suggestions */
    var collections = (data.resources && data.resources.results && data.resources.results.collections) || [];
    if (collections.length) {
      html += '<div class="mc-search-dropdown__section">';
      html += '<p class="mc-search-dropdown__label">Collections</p>';
      collections.slice(0, 4).forEach(function (col) {
        html += '<a href="' + col.url + '" class="mc-search-dropdown__col-item">';
        if (col.featured_image && col.featured_image.url) {
          html += '<img src="' + col.featured_image.url + '" alt="' + escapeHtml(col.title) + '" width="36" height="36" loading="lazy">';
        } else {
          html += '<span class="mc-search-dropdown__col-thumb"></span>';
        }
        html += '<span>' + escapeHtml(col.title) + '</span>';
        html += '</a>';
      });
      html += '</div>';
    }

    /* Product suggestions */
    var products = (data.resources && data.resources.results && data.resources.results.products) || [];
    if (products.length) {
      html += '<div class="mc-search-dropdown__section">';
      html += '<p class="mc-search-dropdown__label">Products</p>';
      products.slice(0, 5).forEach(function (prod) {
        var price = prod.price ? formatMoney(prod.price) : '';
        var img   = prod.featured_image && prod.featured_image.url ? prod.featured_image.url : '';
        html += '<a href="' + prod.url + '" class="mc-search-dropdown__prod-item">';
        if (img) {
          html += '<img src="' + img + '" alt="' + escapeHtml(prod.title) + '" width="48" height="48" loading="lazy">';
        } else {
          html += '<span class="mc-search-dropdown__prod-thumb"></span>';
        }
        html += '<span class="mc-search-dropdown__prod-info">';
        html += '<span class="mc-search-dropdown__prod-title">' + escapeHtml(prod.title) + '</span>';
        if (price) html += '<span class="mc-search-dropdown__prod-price">' + price + '</span>';
        html += '</span>';
        html += '</a>';
      });
      html += '</div>';

      /* View all results link */
      html += '<a href="/search?q=' + encodeURIComponent(query) + '" class="mc-search-dropdown__view-all">View all results for &ldquo;' + escapeHtml(query) + '&rdquo; &rarr;</a>';
    }

    if (!collections.length && !products.length) {
      html += '<p class="mc-search-dropdown__empty">No results for &ldquo;' + escapeHtml(query) + '&rdquo;</p>';
    }

    return html;
  }

  /* ENG-13: every suggestion request gets a number (and an AbortController). A response is drawn only if it is
     still the newest request AND the input still holds the text it was asked for, so a slow older answer can never
     replace a newer one, and clearing the box / Escape / clicking away cancels whatever is pending. */
  function cancelPending(state, dropdown) {
    state.seq++;
    clearTimeout(debounceTimer);
    if (state.ctrl) { try { state.ctrl.abort(); } catch (e) { /* no-op */ } state.ctrl = null; }
    closeDropdown(dropdown);
  }

  function fetchSuggestions(q, dropdown, state, input) {
    if (q.length < 2) { cancelPending(state, dropdown); return; }

    try {
      if (window.mcTrack && typeof window.mcTrack.search === 'function') {
        window.mcTrack.search(q);
      }
    } catch (e) { /* no-op */ }

    var url = '/search/suggest.json?q=' + encodeURIComponent(q)
      + '&resources[type]=product,collection'
      + '&resources[limit]=6'
      + '&resources[options][unavailable_products]=last';

    var mine = ++state.seq;
    if (state.ctrl) { try { state.ctrl.abort(); } catch (e) { /* no-op */ } }
    var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
    state.ctrl = ctrl;

    fetch(url, { headers: { 'Accept': 'application/json' }, signal: ctrl ? ctrl.signal : undefined })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!dropdown) return;
        if (mine !== state.seq) return;                       /* a newer request (or a cancel) has happened */
        if (input && input.value.trim() !== q) return;        /* the text has changed since this was asked */
        if (state.ctrl === ctrl) state.ctrl = null;
        dropdown.innerHTML = buildDropdownHTML(data, q);
        dropdown.classList.add('mc-search-dropdown--open');
        /* ENG-09: tell screen-reader users that suggestions appeared (polite) */
        var rr = (data && data.resources && data.resources.results) || {};
        var nSug = Math.min((rr.collections || []).length, 4) + Math.min((rr.products || []).length, 5);
        announce(nSug ? nSug + (nSug === 1 ? ' suggestion' : ' suggestions') + ' available.' : 'No results for ' + q + '.');
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;         /* cancelled on purpose */
        if (mine === state.seq) closeDropdown(dropdown);
      });
  }

  /* Initialize predictive search on every search input present. */
  function initSearchInput(input) {
    if (!input || input.dataset.mcSearchBound) return;
    input.dataset.mcSearchBound = '1';

    var form = input.closest('form');
    /* Find an existing dropdown as a sibling, else create one. */
    var dropdown = (form && form.parentNode && form.parentNode.querySelector('.mc-search-dropdown'))
      || document.createElement('div');
    if (!dropdown.classList.contains('mc-search-dropdown')) {
      dropdown.className = 'mc-search-dropdown';
      if (form) {
        form.parentNode.style.position = form.parentNode.style.position || 'relative';
        form.parentNode.appendChild(dropdown);
      }
    }

    var state = { seq: 0, ctrl: null };
    var debounced = debounce(function () {
      fetchSuggestions(input.value.trim(), dropdown, state, input);
    }, 220);

    input.addEventListener('input', function () {
      /* Cleared (or too short): cancel at once, don't wait for the debounce, so no old answer can reopen the list. */
      if (input.value.trim().length < 2) { cancelPending(state, dropdown); return; }
      debounced();
    });

    input.addEventListener('focus', function () {
      if (input.value.trim().length >= 2) {
        fetchSuggestions(input.value.trim(), dropdown, state, input);
      }
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') cancelPending(state, dropdown);
    });

    document.addEventListener('click', function (e) {
      if (form && !form.contains(e.target) && !dropdown.contains(e.target)) {
        cancelPending(state, dropdown);
      }
    });
  }

  [
    '.mc-search-bar__input',   // dedicated search page
    '.mc-nav__search-input',   // header
    '#mc-mobile-search-input'  // mobile drawer
  ].forEach(function (sel) {
    document.querySelectorAll(sel).forEach(initSearchInput);
  });

  function initFormatChips() {
    var allowed = {};
    MC_SEARCH_FORMAT_CHIPS.forEach(function (l) { allowed[l.toLowerCase()] = true; });
    document.querySelectorAll('.mc-search-filters[data-format-chips]').forEach(function (row) {
      var shown = 0, keptActive = 0;
      row.querySelectorAll('[data-chip-label]').forEach(function (chip) {
        var isActive = chip.hasAttribute('data-chip-active');
        var ok = isActive || allowed[(chip.getAttribute('data-chip-label') || chip.textContent || '').trim().toLowerCase()] === true;
        chip.hidden = !ok;
        if (ok) { shown++; if (isActive) keptActive++; }
      });
      /* No format to offer and nothing selected: no row at all (just the count and sort). */
      row.hidden = (shown === 0);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initFormatChips); else initFormatChips();

  /* ── Search Filter Chips ──
     The chips are plain links generated by Shopify (search.filters: url_to_add / url_to_remove), so they keep the
     query, search type and sort and reset the page by themselves. The only thing needing script: "All" when more than
     one value of the same filter is selected (clear every value of that filter in one go). */
  document.addEventListener('click', function (e) {
    var all = e.target.closest && e.target.closest('[data-clear-param]');
    if (!all || e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
    e.preventDefault();
    var url = new URL(window.location.href);
    url.searchParams.delete(all.getAttribute('data-clear-param'));
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  /* ── Sort Dropdown ── */
  var sortSelect = document.querySelector('.mc-search-sort__select');
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  /* ── Add to Cart from Search Card ── */
  document.addEventListener('click', function (e) {
    var atcBtn = e.target.closest('.mc-search-card__atc');
    if (!atcBtn) return;

    var variantId = atcBtn.dataset.variantId;
    if (!variantId) return;

    var original = atcBtn.textContent;
    atcBtn.textContent = 'Adding…';
    atcBtn.disabled    = true;

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify({ id: variantId, quantity: 1, attributes: mcGetReferralAttribute() })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) {
          atcBtn.textContent = 'Error';
          atcBtn.disabled    = false;
          setTimeout(function () { atcBtn.textContent = original; }, 2000);
          return;
        }
        atcBtn.textContent = '✓ Added!';
        try { sessionStorage.setItem('mc_last_added_variant_id', String(data.variant_id || variantId)); } catch (e) { /* no-op */ }

        /* Real fix (ga4-analytics-tracking-prompt.md part 4) — this real, working AJAX add-to-cart path never called mcTrack.addToCart at all. Same shape as the PDP version (assets/monmouth-pdp.js). */
        try {
          if (window.mcTrack && typeof window.mcTrack.addToCart === 'function') {
            window.mcTrack.addToCart({
              id: data.product_id || variantId,
              title: data.product_title || data.title,
              price: (typeof data.price === 'number' ? data.price / 100 : undefined)
            }, 1);
          }
        } catch (e) { /* no-op */ }

        document.dispatchEvent(new CustomEvent('mc:cart-added', { detail: { item: data } }));
        document.dispatchEvent(new CustomEvent('mc:cart-updated'));
        document.dispatchEvent(new CustomEvent('mc:open-cart-drawer'));
        setTimeout(function () {
          atcBtn.textContent = original;
          atcBtn.disabled    = false;
        }, 1600);
      })
      .catch(function () {
        atcBtn.textContent = 'Error';
        atcBtn.disabled    = false;
        setTimeout(function () { atcBtn.textContent = original; }, 2000);
      });
  });

  /* ── Utilities ── */
  function announce(msg) {
    try { if (window.mcAnnounce && msg) window.mcAnnounce(msg); } catch (e) { /* no-op */ }
  }

  /* ENG-09: the search page reloads when a filter chip or the sort is used, so say what the new result list holds
     (only then: a plain search already announces itself through the page title). */
  (function announceFilteredResults() {
    if (!/^\/search\/?$/.test(window.location.pathname)) return;
    var s = window.location.search;
    if (s.indexOf('filter.') === -1 && s.indexOf('sort_by=') === -1) return;
    function go() {
      var el = document.querySelector('.mc-search-meta__count');
      var txt = el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
      if (txt) setTimeout(function () { announce('Results updated: ' + txt); }, 700);
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go); else go();
  })();

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatMoney(amount) {
    return '$' + parseFloat(amount).toFixed(2).replace(/\.00$/, '');
  }

  function getQueryParam(key) {
    return new URLSearchParams(window.location.search).get(key) || '';
  }

})();
